import axios from 'axios'
import type { AuthData } from '../../types/auth/authData'

const googleButtonContainerId = 'google-signin-hidden-container'

let initPromise: Promise<void> | null = null
let googleApiKey: string | null = null
let pendingResolve: ((value: AuthData) => void) | null = null
let pendingReject: ((reason?: any) => void) | null = null

const gsiScriptSrc = 'https://accounts.google.com/gsi/client'
let gsiLoadPromise: Promise<void> | null = null

function loadGoogleGsiScriptOnce(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google GSI can only be loaded in the browser'))
  }

  if ((window as any).google?.accounts?.id) return Promise.resolve()

  if (gsiLoadPromise) return gsiLoadPromise

  gsiLoadPromise = new Promise<void>((resolve, reject) => {
    const waitUntilReady = () => {
      const start = Date.now()
      const tick = () => {
        if ((window as any).google?.accounts?.id) return resolve()
        if (Date.now() - start > 10_000)
          return reject(new Error('Timed out waiting for Google GSI to become ready'))
        setTimeout(tick, 50)
      }
      tick()
    }

    const existing = document.querySelector(
      `script[src="${gsiScriptSrc}"]`
    ) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', waitUntilReady, { once: true })
      existing.addEventListener(
        'error',
        () => reject(new Error(`Failed to load script: ${gsiScriptSrc}`)),
        { once: true }
      )
      waitUntilReady()
      return
    }

    const s = document.createElement('script')
    s.src = gsiScriptSrc
    s.async = true
    s.defer = true
    s.onload = waitUntilReady
    s.onerror = () => reject(new Error(`Failed to load script: ${gsiScriptSrc}`))
    document.head.appendChild(s)
  }).catch(err => {
    gsiLoadPromise = null
    throw err
  })

  return gsiLoadPromise
}

function ensureGoogleInitialized(clientId: string): Promise<void> {
  if (initPromise) return initPromise

  initPromise = (async () => {
    await loadGoogleGsiScriptOnce()

    if (!window.google?.accounts?.id) {
      initPromise = null
      throw new Error('Google Identity Services client not loaded')
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response: any) => {
        if (!pendingResolve || !pendingReject) return

        if (!response || !response.credential) {
          pendingReject(new Error('No credential returned from Google'))
          pendingResolve = null
          pendingReject = null
          return
        }

        try {
          if (!googleApiKey) throw new Error('Missing Google API key')
          const googleIdToken = response.credential
          const url = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithIdp?key=${googleApiKey}`
          const payload = {
            postBody: `id_token=${googleIdToken}&providerId=google.com`,
            requestUri: window.location.origin,
            returnSecureToken: true,
          }

          const { data } = await axios.post(url, payload, {
            headers: { 'Content-Type': 'application/json' },
          })

          const authData: AuthData = {
            kind: data.kind,
            localId: data.localId,
            email: data.email,
            displayName: data.displayName,
            idToken: data.idToken,
            registered: true,
            profilePicture: data.photoUrl,
            refreshToken: data.refreshToken,
            expiresIn: data.expiresIn,
          }

          pendingResolve(authData)
        } catch (err) {
          pendingReject(err)
        } finally {
          pendingResolve = null
          pendingReject = null
        }
      },
      ux_mode: 'popup',
    })

    let container = document.getElementById(googleButtonContainerId) as HTMLElement | null
    if (!container) {
      container = document.createElement('div')
      container.id = googleButtonContainerId
      container.style.position = 'fixed'
      container.style.top = '-9999px'
      container.style.left = '-9999px'
      container.style.pointerEvents = 'none'
      document.body.appendChild(container)
    }

    window.google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
    })
  })().catch(err => {
    initPromise = null
    throw err
  })

  return initPromise
}

export async function signInWithGoogleAsync(): Promise<AuthData> {
  const clientId = import.meta.env.VITE_AUTH_GOOGLE_CLIENT_ID
  const apiKey = import.meta.env.VITE_AUTH_GIP_API_KEY
  if (!clientId || !apiKey) throw new Error('Missing Google Sign-In configuration')

  googleApiKey = apiKey

  return new Promise<AuthData>(async (resolve, reject) => {
    pendingResolve = resolve
    pendingReject = reject

    try {
      await ensureGoogleInitialized(clientId)
    } catch (e) {
      pendingResolve = null
      pendingReject = null
      reject(e)
      return
    }

    let container = document.getElementById(googleButtonContainerId)
    let button = container?.querySelector('div[role="button"], button') as HTMLElement | null

    if (!button) {
      initPromise = null
      try {
        await ensureGoogleInitialized(clientId)
        container = document.getElementById(googleButtonContainerId)
        button = container?.querySelector('div[role="button"], button') as HTMLElement | null
      } catch (e) {
        pendingResolve = null
        pendingReject = null
        reject(e)
        return
      }
    }

    if (!button) {
      pendingResolve = null
      pendingReject = null
      reject(new Error('Google Sign-In button not found'))
      return
    }

    button.click()

    const timeout = setTimeout(() => {
      if (pendingReject) {
        pendingReject(new Error('Google Sign-In was cancelled or timed out'))
        pendingResolve = null
        pendingReject = null
      }
      clearTimeout(timeout)
    }, 120000)
  })
}
